/**
 * @file ローディング状態の管理フック
 *
 * API呼び出し中のローディング表示を制御するために、URLベースでローディング中の
 * APIリストを管理する。複数APIが同時に走っている場合も個別に追跡できる。
 */
import loadingState from '@/states/loadingState';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

/**
 * loadingState設定用フック
 * @param url 実行するAPIのURL
 * @returns startLoading 開始
 * @returns finishLoading 終了
 */
const useSetLoading = () => {
  const setIsLoading = useSetRecoilState(loadingState);

  const startLoading = useCallback(
    (url: string) => {
      setIsLoading((prev) => prev.concat([url]));
    },
    [setIsLoading]
  );

  const finishLoading = useCallback(
    (url: string) => {
      setIsLoading((prev) => prev.filter((str) => str !== url));
    },
    [setIsLoading]
  );

  /**
   * ローディング状態を取得する。
   * @param keys 特定のローディング状態を取得する場合に使用する
   * @returns ローディング状態が存在する場合は、trueを返却する。それ以外は、falseを返却する。
   */
  const isLoading = useCallback(
    (keys?: string | string[]): boolean => {
      // 最新の状態を取得する。
      let state: string[] = [];
      setIsLoading((prev) => {
        state = prev;
        return prev;
      });
      // 取得した情報で表示／非表示の判断処理を実施する。
      if (state.length === 0) {
        return false;
      }
      if (typeof keys === 'string') {
        return state.includes(keys);
      }
      if (Array.isArray(keys)) {
        return state.some((e) => keys.includes(e));
      }
      return true;
    },
    [setIsLoading]
  );

  return { isLoading, startLoading, finishLoading };
};

export default useSetLoading;
